import { useState } from "react";
import { useGameStore } from "../store/gameStore";
import { login, register } from "../services/auth";

/**
 * LOGIN/REGISTER MODAL COMPONENT
 * Handles user authentication and registration
 */

export function LoginModal() {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showLoginModal = useGameStore((state) => state.showLoginModal);
  const setShowLoginModal = useGameStore((state) => state.setShowLoginModal);
  const setAuth = useGameStore((state) => state.setAuth);

  // Validation helper
  const validateForm = (): string | null => {
    if (!username.trim() || !password.trim()) {
      return "Username and password are required";
    }

    if (username.length < 3 || username.length > 20) {
      return "Username must be 3-20 characters";
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      return "Username can only contain letters, numbers, and underscores";
    }

    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }

    if (!isLogin && password !== confirmPassword) {
      return "Passwords do not match";
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const authFn = isLogin ? login : register;
      const response = await authFn(username.trim(), password);

      // Store auth data in Zustand store
      setAuth(
        {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        },
        response.user,
      );

      // Close modal and reset form
      setShowLoginModal(false);
      setUsername("");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Authentication failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowLoginModal(false);
    setError(null);
    setUsername("");
    setPassword("");
    setConfirmPassword("");
  };

  if (!showLoginModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg p-8 w-full max-w-md border border-slate-700">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            {isLogin ? "Login" : "Register"}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white text-2xl leading-none transition"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-danger/20 border border-danger text-danger px-4 py-3 rounded-lg mb-4 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-lg font-bold flex-shrink-0">⚠</span>
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 transition"
              placeholder="3-20 characters"
              autoComplete="username"
            />
            {!isLogin && (
              <p className="text-xs text-gray-400 mt-1">
                Letters, numbers, and underscores only
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 transition"
              placeholder="At least 8 characters"
              autoComplete={isLogin ? "current-password" : "new-password"}
            />
            {!isLogin && (
              <p className="text-xs text-gray-400 mt-1">
                Minimum 8 characters for security
              </p>
            )}
          </div>

          {/* Confirm Password (Register only) */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 transition"
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={
              loading ||
              !username ||
              !password ||
              (!isLogin && !confirmPassword)
            }
            className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                {isLogin ? "Logging in..." : "Creating account..."}
              </span>
            ) : isLogin ? (
              "Login"
            ) : (
              "Register"
            )}
          </button>
        </form>

        {/* Toggle Mode */}
        <div className="mt-6 text-center text-sm">
          <p className="text-gray-400">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
          </p>
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setError(null);
              setPassword("");
              setConfirmPassword("");
            }}
            className="mt-2 text-primary-400 hover:text-primary-300 font-semibold transition disabled:opacity-50"
            disabled={loading}
          >
            {isLogin ? "Create one now" : "Login instead"}
          </button>
        </div>
      </div>
    </div>
  );
}
